import { getEnvironmentConfig } from "../adapters/environment";

export interface AutoReconnectConfig {
  enabled: boolean;
  maxRetries: number;
  initialDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
}

export interface HeartbeatConfig {
  enabled: boolean;
  interval: number;
  timeout: number;
}

export interface WebSocketConfig {
  autoReconnect: AutoReconnectConfig;
  heartbeat: HeartbeatConfig;
}

export const defaultWebSocketConfig: WebSocketConfig = {
  autoReconnect: {
    enabled: true,
    maxRetries: 10,
    initialDelay: 1000,
    maxDelay: 30000,
    backoffMultiplier: 1.5,
  },
  heartbeat: {
    enabled: true,
    interval: 30000,
    timeout: 10000,
  },
};

export const aggressiveReconnectConfig: WebSocketConfig = {
  autoReconnect: {
    enabled: true,
    maxRetries: 10,
    initialDelay: 2000,
    maxDelay: 30000,
    backoffMultiplier: 2.0,
  },
  heartbeat: {
    enabled: true,
    interval: 30000,
    timeout: 10000,
  },
};

export const conservativeReconnectConfig: WebSocketConfig = {
  autoReconnect: {
    enabled: true,
    maxRetries: 5,
    initialDelay: 2000,
    maxDelay: 60000,
    backoffMultiplier: 2.0,
  },
  heartbeat: {
    enabled: true,
    interval: 60000,
    timeout: 15000,
  },
};

let globalConfig: WebSocketConfig = { ...defaultWebSocketConfig };

export function applyWebSocketConfig(config: WebSocketConfig): void {
  globalConfig = { ...config };
}

export function initializeWebSocketConfig(): void {
  const { environment } = getEnvironmentConfig();

  switch (environment) {
    case "local":
      applyWebSocketConfig(aggressiveReconnectConfig);
      break;
    case "staging":
    case "pre":
      applyWebSocketConfig(defaultWebSocketConfig);
      break;
    case "production":
    default:
      applyWebSocketConfig(conservativeReconnectConfig);
      break;
  }
}

export function getWebSocketConfig(): WebSocketConfig {
  return { ...globalConfig };
}

export function configureAutoReconnect(
  config: Partial<AutoReconnectConfig>
): void {
  globalConfig.autoReconnect = {
    ...globalConfig.autoReconnect,
    ...config,
  };
}

export function configureHeartbeat(config: Partial<HeartbeatConfig>): void {
  globalConfig.heartbeat = {
    ...globalConfig.heartbeat,
    ...config,
  };
}

export function resetWebSocketConfig(): void {
  globalConfig = { ...defaultWebSocketConfig };
}

export function disableAutoReconnect(): void {
  configureAutoReconnect({
    enabled: false,
  });
}

export function enableAutoReconnect(): void {
  configureAutoReconnect({
    enabled: true,
  });
}
